import { AnyObject, Id, Optional } from '@vimeo/core/types/generic';

import { EComposerSessionType, TComposerSessionId } from '../types/composer_session';
import { encodeQueryObjectAsString } from '../utils/general/url_query';
import { transformSessionTypeToUri } from '../utils/vimeo/links';
import { environmentConfig } from './EnvironmentConfig';

export const vimeoConfig = {
  USER: {
    DEFAULT_LOGO_URL(width: number, height?: number) {
      return `${
        environmentConfig.IS_DEV ? vimeoConfig.CDN.DEV : vimeoConfig.CDN.PROD
      }/portrait/defaults-blue_${width}x${height ?? width}.png`;
    },
  },
  PATH: {
    /**
     * Clip.
     */
    CLIP_VIEWER_PAGE(clipId: Id): string {
      return `/${clipId}`;
    },
    CLIP_SETTINGS_PAGE(clipId: Id, config: { trim?: boolean } = {}): string {
      return `/manage/videos/${clipId}${config.trim ? '/trim' : ''}`;
    },
    CLIP_BROADCASTER_PAGE(clipId: Id, queryObject: Optional<AnyObject> = null): string {
      return `/manage/videos/${clipId}/registration${encodeQueryObjectAsString(queryObject)}`;
    },
    /**
     * Album.
     */
    SHOWCASE_MANAGE_URL(albumId: number): string {
      return `${window.location.origin}/manage/showcases/${albumId}`;
    },
    SHOWCASE_VIEW_URL(albumId: number): string {
      return `${window.location.origin}/showcase/${albumId}`;
    },
    /**
     * Event.
     */
    BROADCASTER_PAGE(
      sessionType: EComposerSessionType,
      sessionId: Id,
      queryObject: Optional<AnyObject> = null,
    ): string {
      return `/live/broadcaster/${transformSessionTypeToUri(
        sessionType,
      )}/${sessionId}${encodeQueryObjectAsString(queryObject)}`;
    },
    RTMP_PAGE(
      sessionType: EComposerSessionType,
      sessionId: Id,
      queryObject: Optional<AnyObject> = null,
    ): string {
      return `/live/rtmp/${transformSessionTypeToUri(
        sessionType,
      )}/${sessionId}${encodeQueryObjectAsString(queryObject)}`;
    },
    EVENT_VIEWER_PAGE(eventId: Id, hash: Optional<string> = null): string {
      return `${window.location.origin}/event/${eventId}${hash ? '/' + hash : ''}`;
    },
    INTERACTION_TOOLS_PAGE(
      sessionType: EComposerSessionType,
      sessionId: TComposerSessionId,
      queryObject: Optional<AnyObject> = null,
    ): string {
      return `/live/interaction_tools/${transformSessionTypeToUri(
        sessionType,
      )}/${sessionId}${encodeQueryObjectAsString(queryObject)}`;
    },
    COMPLETED_PAGE(
      sessionType: EComposerSessionType,
      sessionId: Id,
      queryObject: Optional<AnyObject> = null,
    ): string {
      return `/live/completed/${transformSessionTypeToUri(
        sessionType,
      )}/${sessionId}${encodeQueryObjectAsString(queryObject)}`;
    },
    VIMEO_OTT_CHANNEL_VIEW_URL(subdomain: string): string {
      return `https://${subdomain}.vhx.tv/products`;
    },
    VIMEO_OTT_CHANNEL_MANAGE_URL(subdomain: string, ottEventId: number): string {
      return `https://${subdomain}.vhx.tv/admin/manage/events/${ottEventId}/general`;
    },
    /**
     * Misc.
     */
    REQUEST_UPGRADE_PATH: '/upgrade#pricing',
    REQUEST_DEMO_UPGRADE_BUTTON_PATH: '/upgrade?context=events&mkc=368265-sl-se',
    CONTACT_US_FORM_FROM_LIVE: '/enterprise-contact-us-modal',
    CONTACT_US_FORM_EVENT:
      '/enterprise-contact-us-modal?left_side_content_type=vimeo_events&mkc=368ip-events_t1t2-mp&more_plan_details=hide',
    FAIL_SAFE_RTMP_URL: 'rtmp://localhost:1937/live',
    CONNECT_OTT_DESTINATION_URL: '/manage/ott',
  },
  // todo: Probably should be in env config.
  CDN: {
    PROD: 'https://i.vimeocdn.com',
    DEV: 'https://devi.vimeocdn.com',
    ASSETS: {
      ONBOARDING_TOUR_MODAL_BG: '/custom_asset/3276',
    },
  },
  VIDEOTHUMB: {
    DEFAULT_URL(): string {
      return 'https://i.vimeocdn.com/video/default';
    },
    URL_AT_RATIO(url: string, width: number, heightRatio: number = 9 / 16): string {
      const base = url.split('_')[0];
      const height = Math.round(width * heightRatio);
      return base + `_${width}x${height}`;
    },
    URL_AT_RATIO_FOR_ID(
      id: string,
      height: number,
      ratio: number = 16 / 9,
      forceProd?: boolean,
    ): string {
      return `${
        environmentConfig.IS_COM || forceProd ? vimeoConfig.CDN.PROD : vimeoConfig.CDN.DEV
      }/video/${id}_${Math.floor(height * ratio)}x${Math.floor(height)}`;
    },
  },
  SUPPORT: {
    VIMEO_HELP_CENTER:
      '/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426925620497',
    ENCODER_GUIDE:
      '/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426924775953-Encoder-guide',
    AUTO_CC_LEARN_MORE:
      '/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426960030353',
    GUEST_PAGE_LEARN_MORE:
      '/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426959504401',
    DEVICE_PERMISSIONS:
      '/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426959565457',
    SCREEN_USAGE: '/help/sso?redirect_to=https://help.vimeo.com/hc/en-us/articles/12426962678545',
    UPGRADE_YOUR_PLAN: '/help/sso?redirect_to=https://vimeo.com/enterprise/contact-us',
    UPGRADE_YOUR_PLAN_DIRECT: '/enterprise/contact-us',
    CONTACT_SALES_EMAIL: 'mailto:accountmanagement@vimeo.com',
  },
};
